import { FC, useEffect, useMemo, useState, useCallback } from "react";
import { Controller, useForm } from "react-hook-form";
import { Box, Grid, Stack, Typography, FormControl, FormControlLabel, RadioGroup, Radio, IconButton, styled, Button, Tooltip } from "@mui/material";
import { Close as CloseIcon, Info as InfoIcon } from "@mui/icons-material";
import { Range } from "react-date-range";
import { format } from "date-fns";

import { ModalV2, TextInput, SelectBox, MultiAutocomplete, SingleAutocomplete, DateRangeFilter, SkeletonLoader } from "@/components";
import VirtualizedChipList from "@/components/virtualized-chip-list";
import InputErrorMessage from "@/components/input-error-message";
import { 
  ReconciliationFormData, 
  AdjustmentType,
  DutyAdjustmentEntry,
  DutyAdjustmentOperation,
  CH99ChangeMode,
  CH99Transformation,
  CombinedAdjustmentData,
  transformFiltersToBackend,
  createEmptyTransformation
} from "@/types/reconciliation";
import { validateReconciliationForm, validateHtsCode } from "@/utils/reconciliation-validation";
import { validateField, ValidationRules, ValidationPatterns, debounce } from "@/utils/form-validation";
import { getLastYearToDateRange } from "@/utils";
import { 
  useGetHTSCodesForDropdownQuery,
  useGetCountriesForDropdownQuery, 
  useGetManufacturersForDropdownQuery,
  useGetTotalReconcilationQuery
} from "@/services";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";

// Shared Toggle Header Component
const HTSCodeToggleHeader: FC<{
  codeType: 'mfn' | 'ch99';
  onCodeTypeChange: (type: 'mfn' | 'ch99') => void;
}> = ({ codeType, onCodeTypeChange }) => (
  <Box sx={{ 
    position: 'sticky', 
    top: 0, 
    bgcolor: 'background.paper',
    zIndex: 1,
    borderBottom: '1px solid',
    borderColor: 'divider',
    p: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 1
  }}>
    <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1 }}>
      Code Type:
    </Typography>
    <Button
      size="small"
      variant={codeType === 'mfn' ? 'contained' : 'outlined'}
      onClick={() => onCodeTypeChange('mfn')}
    >
      MFN Codes
    </Button>
    <Button
      size="small"
      variant={codeType === 'ch99' ? 'contained' : 'outlined'}
      onClick={() => onCodeTypeChange('ch99')}
    >
      Chapter 99
    </Button>
  </Box>
);

// HTSCodesWithToggle Component
interface HTSCodesWithToggleProps {
  value: string[];
  onChange: (value: string[]) => void;
  codeType: 'mfn' | 'ch99';
  onCodeTypeChange: (type: 'mfn' | 'ch99') => void;
  allOptions: { value: string; label: string; type: string }[];
  isLoading: boolean;
}

const HTSCodesWithToggle: FC<HTSCodesWithToggleProps> = ({
  value,
  onChange,
  codeType,
  onCodeTypeChange,
  allOptions,
  isLoading
}) => {
  // Filter options based on current toggle selection
  const currentOptions = codeType === 'mfn' 
    ? allOptions.filter(code => code.type === 'hts')
    : allOptions.filter(code => code.type === 'ch99Code' || code.type === 'ch99Code2');
  
  return (
    <MultiAutocomplete
      value={value}
      onChange={onChange}
      label="HTS Codes (supports wildcards: 1234*)"
      options={currentOptions.map(opt => opt.value)}
      placeholder={isLoading ? "Loading codes..." : "Type HTS codes or select from list..."}
      helperText={`${currentOptions.length} ${codeType === 'mfn' ? 'MFN' : 'Chapter 99'} codes available`}
      customHeader={
        <HTSCodeToggleHeader
          codeType={codeType}
          onCodeTypeChange={onCodeTypeChange}
        />
      }
    />
  );
};

// Styled components to match legacy questionnaire system
const StyledInputContainer = styled(Stack)({
  flexDirection: "row",
  border: "1.2px solid #DEDEDE",
  borderRadius: "12px",
  padding: 24,
  justifyContent: "space-between",
  alignItems: "center",
});

// Utility function for number formatting (from legacy system)
const numberWithCommas = (num: number): string => {
  return Math.floor(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

// Currency symbol mapping (from legacy system)
const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    'USD': '$',
    'MXN': '$',
    'CAD': '$',
    'GBP': '£',
    'EUR': '€',
    'CNY': '¥',
    'JPY': '¥',
    'AUD': '$',
    'CHF': 'CHF',
  };
  return symbols[currency] || '$';
};

interface ReconciliationConfigModalProps {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  onSubmit: (data: ReconciliationFormData) => void;
  isLoading?: boolean;
}

interface FormData {
  reconFlag: 'Y' | 'N' | '';
  htsCodes: string[];
  originCodes: string[];
  manufacturerIds: string[];
  entryLines: string[];
  adjustmentType: AdjustmentType;
  adjustmentMode: 'percentage' | 'amount';
  percentage: string;
  amount: string;
  currency: string;
  dutyAdjustments: DutyAdjustmentEntry[];
}

const ReconciliationConfigModal: FC<ReconciliationConfigModalProps> = ({
  show,
  setShow,
  onSubmit,
  isLoading = false,
}) => {
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const { user } = useAppSelector((state) => state.auth);

  // Role-based logic for TD_ADMIN
  const isTDAdmin = useMemo(() => {
    return user?.userRole === roles.TDADMIN;
  }, [user]);

  // Date range state using the same pattern as dashboard analytics
  const [dateRange, setDateRange] = useState<Pick<Range, "startDate" | "endDate">>({
    ...getLastYearToDateRange(),
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    setValue,
    trigger,
  } = useForm<FormData>({
    defaultValues: {
      reconFlag: '',
      htsCodes: [],
      originCodes: [],
      manufacturerIds: [],
      entryLines: [],
      adjustmentType: 'value_percentage',
      adjustmentMode: 'percentage',
      percentage: '',
      amount: '',
      currency: 'USD',
      dutyAdjustments: [],
    },
    mode: 'onChange', // Enable onChange validation
  });

  const adjustmentType = watch('adjustmentType');
  const adjustmentMode = watch('adjustmentMode');
  const amount = watch('amount');
  const percentage = watch('percentage');
  const currency = watch('currency');
  const reconFlag = watch('reconFlag');
  const htsCodes = watch('htsCodes');
  const originCodes = watch('originCodes');
  const manufacturerIds = watch('manufacturerIds');
  const entryLines = watch('entryLines');
  const dutyAdjustments = watch('dutyAdjustments');

  // Form validation rules
  const formValidationRules: ValidationRules = useMemo(() => ({
    percentage: adjustmentType === 'value_percentage' && adjustmentMode === 'percentage' ? {
      required: true,
      pattern: ValidationPatterns.percentage,
      message: 'Enter a valid percentage (e.g., 5% or -10.5%)'
    } : {},
    amount: (adjustmentType === 'value_percentage' || adjustmentType === 'combined_value_and_duty') && adjustmentMode === 'amount' ? {
      required: true,
      pattern: ValidationPatterns.amount,
      min: 0.01,
      message: 'Enter a valid amount greater than 0'
    } : {},
    currency: adjustmentMode === 'amount' ? {
      required: true,
      message: 'Currency is required when using amount mode'
    } : {},
  }), [adjustmentType, adjustmentMode]);

  // Real-time validation state
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // State for HTS validation errors
  const [htsValidationErrors, setHtsValidationErrors] = useState<Record<number, string>>({});
  
  // State for entry line input
  const [entryLineInputValue, setEntryLineInputValue] = useState('');
  
  // Debounced validation function
  const debouncedValidate = useCallback(
    debounce((field: string, value: any) => {
      if (formValidationRules[field]) {
        const error = validateField(value, formValidationRules[field]);
        setFieldErrors(prev => ({
          ...prev,
          [field]: error || ''
        }));
      }
    }, 300),
    [formValidationRules]
  );
  
  // State for code type toggle (MFN vs CH99)
  const [codeType, setCodeType] = useState<'mfn' | 'ch99'>('mfn');

  // Single API call to get all HTS code types
  const { data: allHTSCodes = [], isLoading: htsLoading } = useGetHTSCodesForDropdownQuery({
    clientId: selectedClient?._id,
    ...(isTDAdmin && { tenantId: selectedTenant?._id }),
    includeChapter99: true, // Always get all types
  });

  // Filter codes based on toggle selection
  const mfnCodes = allHTSCodes.filter(code => code.type === 'hts');
  const ch99Codes = allHTSCodes.filter(code => code.type === 'ch99Code' || code.type === 'ch99Code2');

  // Current options based on toggle
  const htsOptions = codeType === 'mfn' ? mfnCodes : ch99Codes;


  const { data: countryOptions = [] } = useGetCountriesForDropdownQuery({
    clientId: selectedClient?._id || "",
    dateFrom: dateRange.startDate ? format(dateRange.startDate, "yyyy-MM-dd") : undefined,
    dateTo: dateRange.endDate ? format(dateRange.endDate, "yyyy-MM-dd") : undefined,
  }, {
    skip: !selectedClient?._id
  });

  const { data: manufacturerOptions = [] } = useGetManufacturersForDropdownQuery({
    dashboardVisibility: {
      clientId: selectedClient?._id,
      ...(isTDAdmin && { tenantId: selectedTenant?._id }),
    },
  });

  // Convert dates to Unix timestamps for the API
  const dateTo = useMemo(() => {
    if (!dateRange.endDate) return undefined;
    return Math.floor(dateRange.endDate.getTime() / 1000);
  }, [dateRange.endDate]);

  const dateFrom = useMemo(() => {
    if (!dateRange.startDate) return undefined;
    return Math.floor(dateRange.startDate.getTime() / 1000);
  }, [dateRange.startDate]);

  // Get total sum for rate factor calculation (for all value adjustments including combined)
  const shouldFetchTotalSum = (adjustmentType === 'value_percentage' || adjustmentType === 'combined_value_and_duty') && 
                             currency && 
                             dateRange.startDate && 
                             dateRange.endDate && 
                             selectedClient?._id;

  // Extract HTS codes from duty adjustments for total sum calculation
  const dutyAdjustmentHTSCodes = useMemo(() => {
    const codes: string[] = [];
    if ((adjustmentType === 'duty_rate_adjustment' || adjustmentType === 'combined_value_and_duty') && dutyAdjustments) {
      dutyAdjustments.forEach(adj => {
        if (adj.operation === 'code_change' && adj.originalCode) {
          codes.push(adj.originalCode);
        } else if (adj.operation === 'rate_adjustment' && adj.htsCode) {
          codes.push(adj.htsCode);
        }
      });
    }
    return codes;
  }, [adjustmentType, dutyAdjustments]);

  // Prepare query parameters with new filters structure
  const queryParams = useMemo(() => {
    const params: any = {
      currency: currency as any,
      clientId: selectedClient?._id,
      ...(isTDAdmin && { tenantId: selectedTenant?._id }),
      dateTo,
      dateFrom,
    };
    
    // Combine regular HTS filter codes with duty adjustment codes
    const allHTSCodes = [
      ...(htsCodes || []),
      ...dutyAdjustmentHTSCodes
    ];
    
    // Remove duplicates
    const uniqueHTSCodes = [...new Set(allHTSCodes)];
    
    // Build filters object from form data
    const formFilters = {
      reconFlag: reconFlag && (reconFlag === 'Y' || reconFlag === 'N') ? reconFlag : undefined,
      htsCodes: uniqueHTSCodes.length ? uniqueHTSCodes : undefined,
      originCodes: originCodes?.length ? originCodes : undefined,  
      manufacturerIds: manufacturerIds?.length ? manufacturerIds : undefined,
      entryLines: entryLines?.length ? entryLines : undefined
    };
    
    const backendFilters = transformFiltersToBackend(formFilters);
    
    // Only include filters if any are set
    if (Object.keys(backendFilters).length > 0) {
      params.filters = backendFilters;
    }
    
    return params;
  }, [currency, selectedClient?._id, selectedTenant?._id, isTDAdmin, dateTo, dateFrom, reconFlag, htsCodes, originCodes, manufacturerIds, entryLines, dutyAdjustmentHTSCodes]);

  const { data: totalSum, isLoading: totalSumLoading } = useGetTotalReconcilationQuery(queryParams, {
    skip: !shouldFetchTotalSum
  });

  // Calculate rate factor like the old questionnaire system
  const rateFactor = useMemo(() => {
    if (!amount || !totalSum || totalSum === 0) return "0.00";
    const factor = ((parseFloat(amount) / totalSum) * 100);
    return factor.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }, [amount, totalSum]);

  // Calculate adjustment amount from percentage
  const adjustmentAmount = useMemo(() => {
    if (!percentage || !totalSum || totalSum === 0) return 0;
    const percentValue = parseFloat(percentage.replace('%', ''));
    return (totalSum * percentValue) / 100;
  }, [percentage, totalSum]);

  const reconFlagOptions = useMemo(() => [
    { value: 'Y', label: 'Yes - Show only reconciled entries' },
    { value: 'N', label: 'No - Show only non-reconciled entries' },
  ], []);

  const adjustmentTypeOptions = useMemo(() => [
    { value: 'value_percentage', label: 'Value Adjustment' },
    { value: 'duty_rate_adjustment', label: 'Duty Rate Adjustment' },
    { value: 'combined_value_and_duty', label: 'Combined Value and Duty Rate Adjustment' },
  ], []);

  const adjustmentModeOptions = useMemo(() => [
    { value: 'percentage', label: 'Percentage (e.g., 5% or 0.1%)' },
    { value: 'amount', label: 'Dollar Amount' },
  ], []);

  const currencyOptions = useMemo(() => [
    { value: 'USD', label: 'USD - US Dollar' },
    { value: 'EUR', label: 'EUR - Euro' },
    { value: 'GBP', label: 'GBP - British Pound' },
    { value: 'CAD', label: 'CAD - Canadian Dollar' },
      { value: 'MXN', label: 'MXN - Mexican Peso' },
    { value: 'CNY', label: 'CNY - Chinese Yuan' },
  ], []);

  const addDutyAdjustment = (operation: DutyAdjustmentOperation = 'rate_adjustment') => {
    const newEntry: DutyAdjustmentEntry = { 
      operation,
      ...(operation === 'code_change' 
        ? { originalCode: '', targetCode: '', ch99ChangeMode: undefined, newCH99CodeList: [], ch99Transformations: [] }
        : { htsCode: '', newRate: 0 }
      )
    };
    setValue('dutyAdjustments', [...dutyAdjustments, newEntry]);
    trigger('dutyAdjustments');
  };

  const removeDutyAdjustment = (index: number) => {
    const updatedEntries = dutyAdjustments.filter((_, i) => i !== index);
    setValue('dutyAdjustments', updatedEntries);
    trigger('dutyAdjustments'); // Force React Hook Form to re-validate
    
    // Clear validation errors and reindex remaining errors
    const newErrors: Record<number, string> = {};
    Object.entries(htsValidationErrors).forEach(([key, value]) => {
      const keyIndex = parseInt(key);
      if (keyIndex < index) {
        newErrors[keyIndex] = value;
      } else if (keyIndex > index) {
        newErrors[keyIndex - 1] = value;
      }
      // Skip the removed index
    });
    setHtsValidationErrors(newErrors);
  };

  const updateDutyAdjustment = (index: number, field: keyof DutyAdjustmentEntry, value: any) => {
    const updatedEntries = [...dutyAdjustments];
    
    // Handle operation type changes
    if (field === 'operation') {
      const operation = value as DutyAdjustmentOperation;
      updatedEntries[index] = { 
        operation,
        ...(operation === 'code_change' 
          ? { originalCode: '', targetCode: '', ch99ChangeMode: undefined, newCH99CodeList: [], ch99Transformations: [] }
          : { htsCode: '', newRate: 0 }
        )
      };
    } else {
      (updatedEntries[index] as any)[field] = value;
    }
    
    setValue('dutyAdjustments', updatedEntries);
    trigger('dutyAdjustments');

    // Validate HTS codes
    if (field === 'originalCode' || field === 'targetCode' || field === 'htsCode') {
      const validationError = validateHtsCode(value as string);
      setHtsValidationErrors(prev => ({
        ...prev,
        [index]: validationError || ''
      }));
    }
  };

  // CH99 Transformation management functions (for selective mode)
  const addTransformation = (adjustmentIndex: number) => {
    const updatedEntries = [...dutyAdjustments];
    const adjustment = updatedEntries[adjustmentIndex];
    
    if (adjustment.operation === 'code_change') {
      if (!adjustment.ch99Transformations) {
        adjustment.ch99Transformations = [];
      }
      adjustment.ch99Transformations.push(createEmptyTransformation());
      setValue('dutyAdjustments', updatedEntries);
      trigger('dutyAdjustments');
    }
  };

  const removeTransformation = (adjustmentIndex: number, transformIndex: number) => {
    const updatedEntries = [...dutyAdjustments];
    const adjustment = updatedEntries[adjustmentIndex];
    
    if (adjustment.operation === 'code_change' && adjustment.ch99Transformations) {
      adjustment.ch99Transformations = adjustment.ch99Transformations.filter((_, i) => i !== transformIndex);
      setValue('dutyAdjustments', updatedEntries);
      trigger('dutyAdjustments');
    }
  };

  const updateTransformation = (adjustmentIndex: number, transformIndex: number, field: keyof CH99Transformation, value: string) => {
    const updatedEntries = [...dutyAdjustments];
    const adjustment = updatedEntries[adjustmentIndex];
    
    if (adjustment.operation === 'code_change' && adjustment.ch99Transformations && adjustment.ch99Transformations[transformIndex]) {
      (adjustment.ch99Transformations[transformIndex] as any)[field] = value;
      setValue('dutyAdjustments', updatedEntries);
      trigger('dutyAdjustments');
    }
  };

  // CH99 Code List management functions (for replace_all mode)
  const updateCH99CodeList = (adjustmentIndex: number, codes: string[]) => {
    const updatedEntries = [...dutyAdjustments];
    const adjustment = updatedEntries[adjustmentIndex];
    
    if (adjustment.operation === 'code_change') {
      adjustment.newCH99CodeList = codes;
      setValue('dutyAdjustments', updatedEntries);
      trigger('dutyAdjustments');
    }
  };

  const handleFormSubmit = (data: FormData) => {    
    if (!dateRange.startDate || !dateRange.endDate) {
      setFieldErrors(prev => ({
        ...prev,
        dateRange: 'Please select both start and end dates'
      }));
      return;
    }
    
    // Clear date range error if dates are valid
    setFieldErrors(prev => {
      const { dateRange, ...rest } = prev;
      return rest;
    });

    // Check for HTS validation errors
    const hasHtsErrors = Object.values(htsValidationErrors).some(error => error);
    if (hasHtsErrors) {
      alert('Please fix HTS code validation errors before submitting');
      return;
    }

    // Use the dutyAdjustments from the watch hook which has the latest values
    const watchedDutyAdjustments = watch('dutyAdjustments');
    
    // Check if duty adjustments exist for duty rate adjustment type
    if (data.adjustmentType === 'duty_rate_adjustment') {
      if (!watchedDutyAdjustments || watchedDutyAdjustments.length === 0) {
        alert('Please add at least one duty adjustment');
        return;
      }
      
      // Check if all adjustments have required fields using the watched values
      const invalidAdjustments = watchedDutyAdjustments.filter((adj) => {
        if (adj.operation === 'code_change') {
          return !adj.originalCode || !adj.targetCode;
        } else if (adj.operation === 'rate_adjustment') {
          return !adj.htsCode || adj.newRate === undefined || adj.newRate === null;
        }
        return false; // Default case - adjustment is valid
      });
      
      if (invalidAdjustments.length > 0) {
        alert('Please complete all required fields in duty adjustments');
        return;
      }
    }

    // Check for combined adjustment type
    if (data.adjustmentType === 'combined_value_and_duty') {
      if (data.adjustmentMode === 'percentage' && !data.percentage) {
        alert('Please enter a value adjustment percentage');
        return;
      }
      
      if (data.adjustmentMode === 'amount' && (!data.amount || !data.currency)) {
        alert('Please enter an adjustment amount and currency');
        return;
      }
      
      // Duty adjustments are optional for combined type, but if provided, validate them
      if (watchedDutyAdjustments && watchedDutyAdjustments.length > 0) {
        const invalidAdjustments = watchedDutyAdjustments.filter((adj) => {
          if (adj.operation === 'code_change') {
            return !adj.originalCode || !adj.targetCode;
          } else if (adj.operation === 'rate_adjustment') {
            return !adj.htsCode || adj.newRate === undefined || adj.newRate === null;
          }
          return false;
        });
        
        if (invalidAdjustments.length > 0) {
          alert('Please complete all required fields in duty adjustments');
          return;
        }
      } else {
        // If no duty adjustments provided, ensure we send an empty array
        data.dutyAdjustments = [];
      }
    }

    // Extract HTS codes from duty adjustments for filtering preview
    const dutyAdjustmentHTSCodes: string[] = [];
    if (data.adjustmentType === 'duty_rate_adjustment' || data.adjustmentType === 'combined_value_and_duty') {
      data.dutyAdjustments.forEach(adj => {
        if (adj.operation === 'code_change' && adj.originalCode) {
          dutyAdjustmentHTSCodes.push(adj.originalCode);
        } else if (adj.operation === 'rate_adjustment' && adj.htsCode) {
          dutyAdjustmentHTSCodes.push(adj.htsCode);
        }
      });
    }

    // Combine regular HTS filter codes with duty adjustment codes
    const allHTSCodes = [
      ...(data.htsCodes || []),
      ...dutyAdjustmentHTSCodes
    ];

    // Remove duplicates
    const uniqueHTSCodes = [...new Set(allHTSCodes)];

    const finalFormData: ReconciliationFormData = {
      dateRange: {
        start: format(dateRange.startDate, 'yyyy-MM-dd'),
        end: format(dateRange.endDate, 'yyyy-MM-dd'),
      },
      filters: {
        reconFlag: data.reconFlag || undefined,
        htsCodes: uniqueHTSCodes.length > 0 ? uniqueHTSCodes : undefined,
        originCodes: data.originCodes.length > 0 ? data.originCodes : undefined,
        manufacturerIds: data.manufacturerIds.length > 0 ? data.manufacturerIds : undefined,
        entryLines: data.entryLines.length > 0 ? data.entryLines : undefined,
      },
      adjustmentType: data.adjustmentType,
      adjustmentData: data.adjustmentType === 'value_percentage'
        ? {
            adjustmentMode: data.adjustmentMode,
            ...(data.adjustmentMode === 'percentage' 
              ? { percentage: data.percentage }
              : { 
                  percentage: `${rateFactor}%`, // Send rate factor as percentage for GQL
                  amount: parseFloat(data.amount), 
                  currency: data.currency,
                  rateFactor: rateFactor,
                  totalSum: totalSum
                }
            )
          }
        : data.adjustmentType === 'combined_value_and_duty'
        ? {
            valueAdjustment: {
              adjustmentMode: data.adjustmentMode,
              ...(data.adjustmentMode === 'percentage' 
                ? { percentage: data.percentage }
                : { 
                    percentage: `${rateFactor}%`, // Send rate factor as percentage for backend
                    amount: parseFloat(data.amount), 
                    currency: data.currency,
                    rateFactor: rateFactor,
                    totalSum: totalSum
                  }
              )
            },
            dutyRateAdjustment: {
              adjustments: data.dutyAdjustments.filter(adj => {
                if (adj.operation === 'code_change') {
                  return adj.originalCode && adj.targetCode;
                } else {
                  return adj.htsCode && adj.newRate !== undefined;
                }
              }).map(adj => {
                if (adj.operation === 'code_change') {
                  return {
                    operation: adj.operation,
                    originalCode: adj.originalCode,
                    targetCode: adj.targetCode,
                    ch99ChangeMode: adj.ch99ChangeMode,
                    newCH99CodeList: adj.newCH99CodeList || [],
                    ch99Transformations: adj.ch99Transformations || []
                  };
                } else {
                  return {
                    operation: adj.operation,
                    htsCode: adj.htsCode,
                    newRate: typeof adj.newRate === 'string' 
                      ? parseFloat(adj.newRate) 
                      : adj.newRate
                  };
                }
              })
            }
          } as CombinedAdjustmentData
        : { 
            adjustments: data.dutyAdjustments.filter(adj => {
              if (adj.operation === 'code_change') {
                return adj.originalCode && adj.targetCode;
              } else {
                return adj.htsCode && adj.newRate !== undefined;
              }
            }).map(adj => {
              if (adj.operation === 'code_change') {
                return {
                  operation: adj.operation,
                  originalCode: adj.originalCode,
                  targetCode: adj.targetCode,
                  ch99ChangeMode: adj.ch99ChangeMode,
                  newCH99CodeList: adj.newCH99CodeList || [],
                  ch99Transformations: adj.ch99Transformations || []
                };
              } else {
                return {
                  operation: adj.operation,
                  htsCode: adj.htsCode,
                  newRate: typeof adj.newRate === 'string' 
                    ? parseFloat(adj.newRate) 
                    : adj.newRate
                };
              }
            })
          },
    };

    
    

    const validationErrors = validateReconciliationForm(finalFormData);
    if (Object.keys(validationErrors).length > 0) {
      
      const errorMessages = Object.entries(validationErrors)
        .map(([field, errors]) => `${field}: ${errors.join(', ')}`)
        .join('\n');
      alert(`Please fix the following errors:\n${errorMessages}`);
      return;
    }

    onSubmit(finalFormData);
    reset();
  };

  const handleClose = () => {
    setShow(false);
    reset();
    setDateRange(getLastYearToDateRange());
    setHtsValidationErrors({});
    setFieldErrors({});
  };

  useEffect(() => {
    if (adjustmentType === 'duty_rate_adjustment' && dutyAdjustments.length === 0) {
      addDutyAdjustment('rate_adjustment');
    }
  }, [adjustmentType, dutyAdjustments.length]);

  return (
    <ModalV2
      show={show}
      setShow={handleClose}
      buttonAction={handleSubmit(handleFormSubmit)}
      isConfirmButtonLoading={isLoading}
      buttonText="Preview Adjustment"
      title="Enhanced Adjustment Configuration"
      subTitle="Configure filters and adjustment parameters for enhanced adjustment processing"
      maxWidth="lg"
    >
      <Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack spacing={3}>
          {/* Adjustment Type Section - Moved to top */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Adjustment Type
            </Typography>
            <Controller
              name="adjustmentType"
              control={control}
              render={({ field }) => (
                <FormControl>
                  <RadioGroup
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e);
                      // Clear HTS codes when switching to duty rate or combined adjustment
                      if (e.target.value !== 'value_percentage') {
                        setValue('htsCodes', []);
                      }
                    }}
                    row
                  >
                    {adjustmentTypeOptions.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio />}
                        label={option.label}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              )}
            />
          </Box>

          {/* Date Range Section */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Date Range
            </Typography>
            <Box>
              <DateRangeFilter 
                value={dateRange} 
                onSelectDate={(range) => {
                  setDateRange(range);
                  // Clear date error when user selects dates
                  if (range.startDate && range.endDate) {
                    setFieldErrors(prev => {
                      const { dateRange, ...rest } = prev;
                      return rest;
                    });
                  }
                }}
                fullWidth={true}
              />
              {fieldErrors.dateRange && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  {fieldErrors.dateRange}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Filters Section */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Filters (Optional)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="reconFlag"
                  control={control}
                  render={({ field }) => (
                    <SelectBox
                      value={field.value}
                      options={[{ value: '', label: 'All entries' }, ...reconFlagOptions]}
                      label="Reconciliation Status"
                      handleChangeValue={field.onChange}
                      error={!!errors.reconFlag}
                      helperText={errors.reconFlag?.message}
                      placeholder="All entries"
                    />
                  )}
                />
              </Grid>
              {/* Only show HTS code filter for value_percentage adjustment type */}
              {adjustmentType === 'value_percentage' ? (
                <Grid item xs={12}>
                  <Controller
                    name="htsCodes"
                    control={control}
                    render={({ field }) => (
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <Box flex={1}>
                            {htsLoading ? (
                              <SkeletonLoader type="form-field" />
                            ) : (
                              <HTSCodesWithToggle
                                value={field.value}
                                onChange={field.onChange}
                                codeType={codeType}
                                onCodeTypeChange={setCodeType}
                                allOptions={allHTSCodes}
                                isLoading={htsLoading}
                              />
                            )}
                          </Box>
                          <Tooltip title="Filter by HTS codes. Supports wildcards (e.g., 1234* for all codes starting with 1234). Chapter 99 codes are included.">
                            <InfoIcon color="action" fontSize="small" sx={{ mt: 2 }} />
                          </Tooltip>
                        </Stack>
                      </Box>
                    )}
                  />
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Box sx={{ p: 2, bgcolor: 'rgba(33, 150, 243, 0.08)', borderRadius: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <InfoIcon color="info" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">
                        HTS code filtering is not available for duty rate adjustments as the specific codes are defined in the adjustment parameters below.
                      </Typography>
                    </Stack>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="originCodes"
                  control={control}
                  render={({ field }) => (
                    <MultiAutocomplete
                      value={field.value}
                      onChange={field.onChange}
                      label="Origin Countries"
                      options={countryOptions.map(opt => opt.value)}
                      placeholder="Select countries..."
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="manufacturerIds"
                  control={control}
                  render={({ field }) => (
                    <MultiAutocomplete
                      value={field.value}
                      onChange={field.onChange}
                      label="Manufacturers"
                      options={manufacturerOptions.map(opt => opt.value)}
                      placeholder="Select manufacturers..."
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="entryLines"
                  control={control}
                  render={({ field }) => (
                    <Box>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="subtitle2" fontWeight={600}>
                            Entry Summary + Line Numbers
                          </Typography>
                          <Tooltip title="Filter by specific entry summary + line numbers. Paste a list from Excel or enter manually. Each entry line number should be the entry summary number concatenated with the line number (e.g., 123456001).">
                            <InfoIcon color="action" fontSize="small" />
                          </Tooltip>
                        </Stack>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => field.onChange([])}
                          disabled={!field.value || field.value.length === 0}
                          sx={{ textTransform: 'none', color: '#575757' }}
                        >
                          Clear All
                        </Button>
                      </Stack>
                      <MultiAutocomplete
                        value={[]}
                        inputValue={entryLineInputValue}
                        onInputChange={(_, newValue) => setEntryLineInputValue(newValue)}
                        onChange={(newValue) => {
                          const existingSet = new Set(field.value);
                          const newLines = newValue.filter((v: string) => !existingSet.has(v));
                          if (newLines.length > 0) {
                            field.onChange([...field.value, ...newLines]);
                          }
                        }}
                        label="Entry Summary + Line Numbers"
                        options={[]}
                        placeholder="Paste or enter entry summary+line numbers..."
                        onPaste={(event: React.ClipboardEvent<HTMLDivElement>) => {
                          event.preventDefault();
                          event.stopPropagation();
                          const pastedText = event.clipboardData.getData('text');
                          const entryLineNumbers = pastedText
                            .split(/[\s,\n\r\t;]+/)
                            .map(line => line.trim())
                            .filter(line => line && line.length > 0);
                          
                          if (entryLineNumbers.length > 0) {
                            const existingSet = new Set(field.value);
                            const newLines = entryLineNumbers.filter(line => !existingSet.has(line));
                            if (newLines.length > 0) {
                              field.onChange([...field.value, ...newLines]);
                            }
                            setTimeout(() => setEntryLineInputValue(''), 50);
                          }
                        }}
                        renderTags={() => null}
                      />
                      <Box sx={{ mt: 1, border: '1px solid #e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                        <VirtualizedChipList
                          key={`entry-lines-${field.value.length}`}
                          items={field.value}
                          onDeleteItem={(lineToDelete: any) => {
                            const newLines = field.value.filter((l: any) => l !== lineToDelete);
                            field.onChange(newLines);
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Adjustment Data Section */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Adjustment Parameters
            </Typography>
            
            {adjustmentType === 'value_percentage' && (
              <Stack spacing={2}>
                {/* Adjustment Mode Selection */}
                <Controller
                  name="adjustmentMode"
                  control={control}
                  render={({ field }) => (
                    <FormControl>
                      <Typography variant="subtitle1" gutterBottom>
                        Adjustment Method
                      </Typography>
                      <RadioGroup
                        value={field.value}
                        onChange={field.onChange}
                        row
                      >
                        {adjustmentModeOptions.map((option) => (
                          <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={<Radio />}
                            label={option.label}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  )}
                />
                
                {/* Percentage Input */}
                {adjustmentMode === 'percentage' && (
                  <Stack spacing={3} sx={{ width: '100%', mb: 3 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Controller
                          name="percentage"
                          control={control}
                          rules={{ 
                            required: adjustmentMode === 'percentage' ? "Percentage is required" : false,
                            pattern: {
                              value: /^-?\d+(\.\d+)?%$/,
                              message: "Must be a percentage (e.g., 5% or -10.5%)"
                            }
                          }}
                          render={({ field }) => (
                            <Box>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <TextInput
                                  label="Value Adjustment Percentage"
                                  type="text"
                                  value={field.value}
                                  handleChangeValue={(value) => {
                                    field.onChange(value);
                                    debouncedValidate('percentage', value);
                                  }}
                                  error={!!errors.percentage || !!fieldErrors.percentage}
                                  placeholder="e.g., 5% or 0.1%"
                                  helperText={fieldErrors.percentage || "Enter percentage adjustment (positive increases, negative decreases)"}
                                />
                                <Tooltip title="Examples: 5% for 5 percent increase, -2.5% for 2.5 percent decrease, 0.1% for small adjustments">
                                  <InfoIcon color="action" fontSize="small" />
                                </Tooltip>
                              </Stack>
                              <InputErrorMessage message={errors.percentage?.message || fieldErrors.percentage} />
                            </Box>
                          )}
                        />
                      </Grid>
                    </Grid>

                    {/* Total Value and Adjustment Amount Display */}
                    {percentage && (
                      <>
                        {/* Total Value of Flagged Transactions */}
                        <StyledInputContainer>
                          <Typography variant="body1">
                            Total Value of Flagged Transactions
                          </Typography>
                          <Typography variant="body1">
                            {totalSumLoading ? (
                              <SkeletonLoader width={100} height={24} />
                            ) : (
                              `${getCurrencySymbol(currency)}${totalSum ? numberWithCommas(totalSum) : "0.00"}`
                            )}
                          </Typography>
                        </StyledInputContainer>

                        {/* Calculated Adjustment Amount */}
                        <StyledInputContainer>
                          <Typography variant="body1">
                            Adjustment Amount ({percentage})
                          </Typography>
                          <Typography variant="body1">
                            {totalSumLoading ? (
                              <SkeletonLoader width={120} height={24} />
                            ) : (
                              `${getCurrencySymbol(currency)}${adjustmentAmount ? numberWithCommas(Math.abs(adjustmentAmount)) : "0.00"}${adjustmentAmount < 0 ? ' (decrease)' : ' (increase)'}`
                            )}
                          </Typography>
                        </StyledInputContainer>
                      </>
                    )}

                    {!totalSumLoading && (!totalSum || totalSum === 0) && percentage && currency && dateRange.startDate && dateRange.endDate && (
                      <Typography variant="body2" color="warning.main" sx={{ textAlign: 'center', mt: 2 }}>
                        No reconciliation data found for the selected criteria
                      </Typography>
                    )}
                  </Stack>
                )}
                
                {/* Legacy-style Amount Input */}
                {adjustmentMode === 'amount' && (
                  <Stack spacing={3} sx={{ width: '100%', mb: 3 }}>
                    
                    {/* Transfer Price Adjustment Figure */}
                    <StyledInputContainer>
                      <Typography variant="body1">
                        Transfer Price Adjustment Figure
                      </Typography>
                      <Box sx={{ display: 'flex', maxWidth: 250 }}>
                        <Controller
                          name="currency"
                          control={control}
                          render={({ field }) => (
                            <SelectBox
                              value={field.value}
                              options={currencyOptions.map(opt => ({ 
                                value: opt.value, 
                                label: `${getCurrencySymbol(opt.value)}${opt.value}` 
                              }))}
                              handleChangeValue={field.onChange}
                              sx={{
                                minWidth: 95,
                                borderRight: '1px solid #E6E7E7',
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '4px 0 0 4px',
                                }
                              }}
                            />
                          )}
                        />
                        <Controller
                          name="amount"
                          control={control}
                          rules={{ 
                            required: adjustmentMode === 'amount' ? "Amount is required" : false,
                            pattern: {
                              value: /^-?\d+(\.\d+)?$/,
                              message: "Must be a valid number"
                            }
                          }}
                          render={({ field }) => (
                            <TextInput
                              type="number"
                              value={field.value}
                              handleChangeValue={(value) => {
                                field.onChange(value);
                                debouncedValidate('amount', value);
                              }}
                              placeholder="0.00"
                              error={!!fieldErrors.amount}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '0 4px 4px 0',
                                  paddingLeft: 0,
                                  border: '1px solid #E6E7E7'
                                }
                              }}
                            />
                          )}
                        />
                      </Box>
                    </StyledInputContainer>

                    {/* Entered Value of Flagged Transactions */}
                    <StyledInputContainer>
                      <Typography variant="body1">
                        Entered Value of Flagged Transactions
                      </Typography>
                      <Typography variant="body1">
                        {totalSumLoading ? (
                          <SkeletonLoader width={100} height={24} />
                        ) : (
                          `${getCurrencySymbol(currency)}${totalSum ? numberWithCommas(totalSum) : "0.00"}`
                        )}
                      </Typography>
                    </StyledInputContainer>

                    {/* Adjustment Factor */}
                    <StyledInputContainer>
                      <Typography variant="body1">
                        Adjustment Factor
                      </Typography>
                      <Typography variant="body1">
                        {rateFactor}%
                      </Typography>
                    </StyledInputContainer>
                    
                    {!totalSumLoading && (!totalSum || totalSum === 0) && amount && currency && dateRange.startDate && dateRange.endDate && (
                      <Typography variant="body2" color="warning.main" sx={{ textAlign: 'center', mt: 2 }}>
                        No reconciliation data found for the selected criteria
                      </Typography>
                    )}
                  </Stack>
                )}
              </Stack>
            )}

            {adjustmentType === 'combined_value_and_duty' && (
              <Stack spacing={3}>
                {/* Value Adjustment Section */}
                <Box>
                  <Typography variant="subtitle1" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                    Step 1: Value Adjustment
                  </Typography>
                  <Grid container spacing={2}>
                    {/* Adjustment Mode Toggle */}
                    <Grid item xs={12}>
                      <Controller
                        name="adjustmentMode"
                        control={control}
                        render={({ field }) => (
                          <SelectBox
                            value={field.value}
                            options={adjustmentModeOptions}
                            label="Adjustment Mode"
                            handleChangeValue={(value) => {
                              field.onChange(value);
                              // Clear values when switching modes
                              if (value === 'percentage') {
                                setValue('amount', '');
                                setValue('currency', 'USD');
                              } else {
                                setValue('percentage', '');
                              }
                            }}
                          />
                        )}
                      />
                    </Grid>
                    
                    {/* Percentage Mode */}
                    {adjustmentMode === 'percentage' && (
                      <Grid item xs={12} sm={6}>
                        <Controller
                          name="percentage"
                          control={control}
                          rules={{ 
                            required: adjustmentType === 'combined_value_and_duty' && adjustmentMode === 'percentage' ? "Percentage is required" : false,
                            pattern: {
                              value: /^-?\d+(\.\d+)?%$/,
                              message: "Must be a percentage (e.g., 5% or -10.5%)"
                            }
                          }}
                          render={({ field }) => (
                            <Box>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <TextInput
                                  label="Value Adjustment Percentage"
                                  type="text"
                                  value={field.value}
                                  handleChangeValue={(value) => {
                                    field.onChange(value);
                                    debouncedValidate('percentage', value);
                                  }}
                                  error={!!errors.percentage || !!fieldErrors.percentage}
                                  placeholder="e.g., 5% or 0.1%"
                                  helperText={fieldErrors.percentage || "Enter percentage adjustment (positive increases value)"}
                                />
                                <Tooltip title="This percentage will be applied to increase the value before duty recalculation">
                                  <InfoIcon color="action" fontSize="small" />
                                </Tooltip>
                              </Stack>
                              <InputErrorMessage message={errors.percentage?.message || fieldErrors.percentage} />
                            </Box>
                          )}
                        />
                      </Grid>
                    )}
                    
                    {/* Amount Mode */}
                    {adjustmentMode === 'amount' && (
                      <>
                        <Grid item xs={12} sm={4}>
                          <Controller
                            name="amount"
                            control={control}
                            rules={{ 
                              required: adjustmentType === 'combined_value_and_duty' && adjustmentMode === 'amount' ? "Amount is required" : false,
                              pattern: {
                                value: /^\d+(\.\d{1,2})?$/,
                                message: "Must be a valid amount (e.g., 100 or 100.50)"
                              }
                            }}
                            render={({ field }) => (
                              <Box>
                                <TextInput
                                  label="Adjustment Amount"
                                  type="text"
                                  value={field.value}
                                  handleChangeValue={(value) => {
                                    field.onChange(value);
                                    debouncedValidate('amount', value);
                                  }}
                                  error={!!errors.amount || !!fieldErrors.amount}
                                  placeholder="e.g., 100000"
                                  helperText={fieldErrors.amount || "Total value to be adjusted"}
                                />
                                <InputErrorMessage message={errors.amount?.message || fieldErrors.amount} />
                              </Box>
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Controller
                            name="currency"
                            control={control}
                            render={({ field }) => (
                              <SelectBox
                                value={field.value}
                                options={currencyOptions}
                                label="Currency"
                                handleChangeValue={field.onChange}
                              />
                            )}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>

                  {/* Show calculated values based on mode */}
                  {((adjustmentMode === 'percentage' && percentage) || (adjustmentMode === 'amount' && amount && totalSum && totalSum > 0)) && (
                    <Stack spacing={2} sx={{ mt: 2 }}>
                      <StyledInputContainer>
                        <Typography variant="body1">
                          Total Value of Selected Transactions
                        </Typography>
                        <Typography variant="body1">
                          {totalSumLoading ? (
                            <SkeletonLoader width={100} height={24} />
                          ) : (
                            `${getCurrencySymbol('USD')}${totalSum ? numberWithCommas(totalSum) : "0.00"}`
                          )}
                        </Typography>
                      </StyledInputContainer>

                      {adjustmentMode === 'percentage' && (
                        <StyledInputContainer>
                          <Typography variant="body1">
                            Value Increase ({percentage})
                          </Typography>
                          <Typography variant="body1">
                            {totalSumLoading ? (
                              <SkeletonLoader width={120} height={24} />
                            ) : (
                              `${getCurrencySymbol('USD')}${adjustmentAmount ? numberWithCommas(Math.abs(adjustmentAmount)) : "0.00"}`
                            )}
                          </Typography>
                        </StyledInputContainer>
                      )}
                      
                      {adjustmentMode === 'amount' && (
                        <>
                          <StyledInputContainer>
                            <Typography variant="body1">
                              Adjustment Amount
                            </Typography>
                            <Typography variant="body1">
                              {`${getCurrencySymbol(currency)}${amount ? numberWithCommas(parseFloat(amount) || 0) : "0.00"}`}
                            </Typography>
                          </StyledInputContainer>
                          <StyledInputContainer>
                            <Typography variant="body1">
                              Rate Factor
                            </Typography>
                            <Typography variant="body1">
                              {rateFactor}%
                            </Typography>
                          </StyledInputContainer>
                        </>
                      )}
                    </Stack>
                  )}
                </Box>

                {/* Duty Rate Adjustment Section */}
                <Box>
                  <Typography variant="subtitle1" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                    Step 2: Duty Rate Adjustments (Applied to Increased Values)
                  </Typography>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      These duty rate changes will be applied to the increased values from Step 1
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <IconButton onClick={() => addDutyAdjustment('code_change')} size="small" sx={{ fontSize: '0.75rem' }}>
                        <Typography variant="button">+ Code Change</Typography>
                      </IconButton>
                      <IconButton onClick={() => addDutyAdjustment('rate_adjustment')} size="small" sx={{ fontSize: '0.75rem' }}>
                        <Typography variant="button">+ Rate Adjustment</Typography>
                      </IconButton>
                    </Stack>
                  </Stack>
                  
                  {dutyAdjustments.map((entry, index) => (
                    <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        {/* Operation Type */}
                        <Grid item xs={12} sm={3}>
                          <SelectBox
                            value={entry.operation}
                            options={[
                              { value: 'code_change', label: 'Change HTS Code' },
                              { value: 'rate_adjustment', label: 'Adjust Duty Rate' }
                            ]}
                            label="Operation Type"
                            handleChangeValue={(value) => updateDutyAdjustment(index, 'operation', value)}
                          />
                        </Grid>

                        {/* Code Change Fields */}
                        {entry.operation === 'code_change' && (
                          <>
                            <Grid item xs={12} sm={4}>
                              <SingleAutocomplete
                                label="Original Code"
                                value={entry.originalCode || ''}
                                options={htsOptions.map(opt => opt.value)}
                                onChange={(value) => updateDutyAdjustment(index, 'originalCode', value)}
                                placeholder={htsLoading ? "Loading codes..." : "Select from list or enter code"}
                                disabled={htsLoading}
                                helperText="Code to be replaced (supports wildcards: 1234*)"
                                error={!!htsValidationErrors[index]}
                                customHeader={
                                  <HTSCodeToggleHeader
                                    codeType={codeType}
                                    onCodeTypeChange={setCodeType}
                                  />
                                }
                              />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <SingleAutocomplete
                                label="Target Code"
                                value={entry.targetCode || ''}
                                options={htsOptions.map(opt => opt.value)}
                                onChange={(value) => updateDutyAdjustment(index, 'targetCode', value)}
                                placeholder={htsLoading ? "Loading codes..." : "Select from list or enter code"}
                                disabled={htsLoading}
                                helperText="New HTS/CH99 code"
                                customHeader={
                                  <HTSCodeToggleHeader
                                    codeType={codeType}
                                    onCodeTypeChange={setCodeType}
                                  />
                                }
                              />
                            </Grid>
                            
                            {/* CH99 Code Change Modes */}
                            <Grid item xs={12}>
                              <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                <Typography variant="body2" fontWeight="bold" gutterBottom>
                                  Chapter 99 Code Handling (Optional)
                                </Typography>
                                
                                {/* Mode Selector */}
                                <FormControl component="fieldset" sx={{ mb: 2 }}>
                                  <RadioGroup
                                    value={entry.ch99ChangeMode || 'none'}
                                    onChange={(e) => {
                                      const mode = e.target.value === 'none' ? undefined : e.target.value as CH99ChangeMode;
                                      updateDutyAdjustment(index, 'ch99ChangeMode', mode);
                                      
                                      // Smart data clearing: preserve user work when switching modes
                                      if (mode === undefined) {
                                        // "No CH99 Changes" - clear all CH99 data to prevent stale submissions
                                        updateDutyAdjustment(index, 'newCH99CodeList', undefined);
                                        updateDutyAdjustment(index, 'ch99Transformations', undefined);
                                      } else if (mode === 'replace_all') {
                                        // Switching to "Replace All" - only clear transformations, preserve code list
                                        updateDutyAdjustment(index, 'ch99Transformations', undefined);
                                        // Initialize newCH99CodeList if it doesn't exist, otherwise preserve existing data
                                        if (!entry.newCH99CodeList) {
                                          updateDutyAdjustment(index, 'newCH99CodeList', []);
                                        }
                                      } else if (mode === 'selective') {
                                        // Switching to "Selective" - only clear code list, preserve transformations
                                        updateDutyAdjustment(index, 'newCH99CodeList', undefined);
                                        // Initialize ch99Transformations if it doesn't exist, otherwise preserve existing data
                                        if (!entry.ch99Transformations) {
                                          updateDutyAdjustment(index, 'ch99Transformations', []);
                                        }
                                      }
                                    }}
                                    row
                                  >
                                    <FormControlLabel value="none" control={<Radio size="small" />} label="No CH99 Changes" />
                                    <FormControlLabel value="replace_all" control={<Radio size="small" />} label="Replace All CH99 Codes" />
                                    <FormControlLabel value="selective" control={<Radio size="small" />} label="Selective Transformations" />
                                  </RadioGroup>
                                </FormControl>

                                {/* Replace All Mode UI */}
                                {entry.ch99ChangeMode === 'replace_all' && (
                                  <Box sx={{ mt: 2 }}>
                                    <Typography variant="caption" color="textSecondary" display="block" mb={1}>
                                      Specify the complete new list of CH99 codes. All existing CH99 codes will be replaced.
                                    </Typography>
                                    <MultiAutocomplete
                                      value={entry.newCH99CodeList || []}
                                      onChange={(codes) => updateCH99CodeList(index, codes)}
                                      label="New CH99 Code List"
                                      options={ch99Codes.map(opt => opt.value)}
                                      placeholder="Select CH99 codes to use..."
                                      helperText="Add as many CH99 codes as needed - rates will be summed"
                                    />
                                  </Box>
                                )}

                                {/* Selective Mode UI */}
                                {entry.ch99ChangeMode === 'selective' && (
                                  <Box sx={{ mt: 2 }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                      <Typography variant="caption" color="textSecondary">
                                        Transform specific CH99 codes. Other codes will be preserved.
                                      </Typography>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => addTransformation(index)}
                                        sx={{ fontSize: '0.7rem' }}
                                      >
                                        + Add Transformation
                                      </Button>
                                    </Stack>
                                    
                                    {entry.ch99Transformations && entry.ch99Transformations.map((transform, transformIndex) => (
                                      <Box key={transformIndex} sx={{ 
                                        mb: 1.5, 
                                        p: 1.5, 
                                        bgcolor: 'grey.50', 
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'grey.300'
                                      }}>
                                        <Grid container spacing={2} alignItems="center">
                                          <Grid item xs={12} sm={5}>
                                            <SingleAutocomplete
                                              label="From (Original CH99)"
                                              value={transform.from}
                                              options={ch99Codes.map(opt => opt.value)}
                                              onChange={(value) => updateTransformation(index, transformIndex, 'from', value)}
                                              placeholder="Select or enter CH99 code"
                                            />
                                          </Grid>
                                          <Grid item xs={12} sm={5}>
                                            <SingleAutocomplete
                                              label="To (New CH99)"
                                              value={transform.to}
                                              options={ch99Codes.map(opt => opt.value)}
                                              onChange={(value) => updateTransformation(index, transformIndex, 'to', value)}
                                              placeholder="Select or enter CH99 code"
                                            />
                                          </Grid>
                                          <Grid item xs={12} sm={2}>
                                            <IconButton
                                              onClick={() => removeTransformation(index, transformIndex)}
                                              size="small"
                                              color="error"
                                            >
                                              <CloseIcon fontSize="small" />
                                            </IconButton>
                                          </Grid>
                                        </Grid>
                                      </Box>
                                    ))}
                                    
                                    {(!entry.ch99Transformations || entry.ch99Transformations.length === 0) && (
                                      <Typography variant="caption" color="textSecondary" display="block" textAlign="center" py={1}>
                                        No transformations. Click "+ Add Transformation" to add one.
                                      </Typography>
                                    )}
                                  </Box>
                                )}
                              </Box>
                            </Grid>
                          </>
                        )}

                        {/* Rate Adjustment Fields */}
                        {entry.operation === 'rate_adjustment' && (
                          <>
                            <Grid item xs={12} sm={4}>
                              <SingleAutocomplete
                                label="HTS Code"
                                value={entry.htsCode || ''}
                                options={htsOptions.map(opt => opt.value)}
                                onChange={(value) => updateDutyAdjustment(index, 'htsCode', value)}
                                placeholder={htsLoading ? "Loading codes..." : "Select from list or enter code"}
                                disabled={htsLoading}
                                helperText={htsValidationErrors[index] || "Code to modify (supports wildcards: 1234*)"}
                                error={!!htsValidationErrors[index]}
                                customHeader={
                                  <HTSCodeToggleHeader
                                    codeType={codeType}
                                    onCodeTypeChange={setCodeType}
                                  />
                                }
                              />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Box>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <TextInput
                                    label="New Rate (0-10)"
                                    type="number"
                                    value={entry.newRate?.toString() || ''}
                                    handleChangeValue={(value) => {
                                      const rate = parseFloat(value) || 0;
                                      if (rate < 0 || rate > 10) {
                                        setFieldErrors(prev => ({
                                          ...prev,
                                          [`dutyRate_${index}`]: 'Rate must be between 0 and 10'
                                        }));
                                      } else {
                                        setFieldErrors(prev => {
                                          const { [`dutyRate_${index}`]: _, ...rest } = prev;
                                          return rest;
                                        });
                                      }
                                      updateDutyAdjustment(index, 'newRate', rate);
                                    }}
                                    placeholder="e.g., 0.05 for 5%"
                                    helperText={fieldErrors[`dutyRate_${index}`] || "Decimal format: 0.05 = 5%"}
                                    error={!!fieldErrors[`dutyRate_${index}`]}
                                  />
                                  <Tooltip title="Enter duty rate in decimal format. 0.05 = 5%, 0.10 = 10%">
                                    <InfoIcon color="action" fontSize="small" />
                                  </Tooltip>
                                </Stack>
                              </Box>
                            </Grid>
                          </>
                        )}

                        {/* Remove Button */}
                        <Grid item xs={12} sm={1}>
                          <IconButton 
                            onClick={() => removeDutyAdjustment(index)}
                            size="small"
                            color="error"
                          >
                            <CloseIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                  
                  {dutyAdjustments.length === 0 && (
                    <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                      No duty adjustments added. Click "+ Code Change" or "+ Rate Adjustment" to add one.
                    </Typography>
                  )}
                </Box>

                {/* Combined Impact Summary */}
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Combined Adjustment Summary:
                  </Typography>
                  <Typography variant="body2" color="textSecondary" component="div">
                    <strong>Step 1: Value Adjustment</strong><br/>
                    Increase all values by {adjustmentMode === 'percentage' ? (percentage || '0%') : (rateFactor || '0') + '%'}
                    <br/><br/>
                    
                    <strong>Step 2: Duty Rate Changes (Applied to Increased Values)</strong>
                    {dutyAdjustments && dutyAdjustments.length > 0 ? (
                      <Box sx={{ mt: 1 }}>
                        {dutyAdjustments.map((adj, index) => (
                          <Box key={index} sx={{ ml: 2, mb: 1 }}>
                            {adj.operation === 'code_change' ? (
                              <>
                                <Typography variant="body2" color="textSecondary">
                                  <strong>Code Change:</strong> {adj.originalCode} → {adj.targetCode}
                                </Typography>
                                {adj.ch99ChangeMode === 'replace_all' && adj.newCH99CodeList && adj.newCH99CodeList.length > 0 && (
                                  <Typography variant="caption" color="textSecondary" display="block" sx={{ ml: 2, mt: 0.5 }}>
                                    • Replace all CH99 with: [{adj.newCH99CodeList.join(', ')}]
                                  </Typography>
                                )}
                                {adj.ch99ChangeMode === 'selective' && adj.ch99Transformations && adj.ch99Transformations.length > 0 && (
                                  <Box sx={{ ml: 2, mt: 0.5 }}>
                                    {adj.ch99Transformations.map((t, tIdx) => (
                                      <Typography key={tIdx} variant="caption" color="textSecondary" display="block">
                                        • Transform: {t.from} → {t.to}
                                      </Typography>
                                    ))}
                                  </Box>
                                )}
                              </>
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                <strong>Rate Adjustment:</strong> {adj.htsCode} → {adj.newRate ? (adj.newRate * 100).toFixed(2) : '0'}%
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary" sx={{ ml: 2, mt: 1 }}>
                        No duty rate changes configured
                      </Typography>
                    )}
                  </Typography>
                </Box>
              </Stack>
            )}

            {adjustmentType === 'duty_rate_adjustment' && (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1">
                    Duty Adjustments
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <IconButton onClick={() => addDutyAdjustment('code_change')} size="small" sx={{ fontSize: '0.75rem' }}>
                      <Typography variant="button">+ Code Change</Typography>
                    </IconButton>
                    <IconButton onClick={() => addDutyAdjustment('rate_adjustment')} size="small" sx={{ fontSize: '0.75rem' }}>
                      <Typography variant="button">+ Rate Adjustment</Typography>
                    </IconButton>
                  </Stack>
                </Stack>
                
                {dutyAdjustments.map((entry, index) => (
                  <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      {/* Operation Type */}
                      <Grid item xs={12} sm={3}>
                        <SelectBox
                          value={entry.operation}
                          options={[
                            { value: 'code_change', label: 'Change HTS Code' },
                            { value: 'rate_adjustment', label: 'Adjust Duty Rate' }
                          ]}
                          label="Operation Type"
                          handleChangeValue={(value) => updateDutyAdjustment(index, 'operation', value)}
                        />
                      </Grid>

                      {/* Code Change Fields */}
                      {entry.operation === 'code_change' && (
                        <>
                          <Grid item xs={12} sm={4}>
                            <SingleAutocomplete
                              label="Original Code"
                              value={entry.originalCode || ''}
                              options={htsOptions.map(opt => opt.value)}
                              onChange={(value) => updateDutyAdjustment(index, 'originalCode', value)}
                              placeholder={htsLoading ? "Loading codes..." : "Select from list or enter code"}
                              disabled={htsLoading}
                              helperText="Code to be replaced (supports wildcards: 1234*)"
                              error={!!htsValidationErrors[index]}
                              customHeader={
                                <HTSCodeToggleHeader
                                  codeType={codeType}
                                  onCodeTypeChange={setCodeType}
                                />
                              }
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <SingleAutocomplete
                              label="Target Code"
                              value={entry.targetCode || ''}
                              options={htsOptions.map(opt => opt.value)}
                              onChange={(value) => updateDutyAdjustment(index, 'targetCode', value)}
                              placeholder={htsLoading ? "Loading codes..." : "Select from list or enter code"}
                              disabled={htsLoading}
                              helperText="New HTS/CH99 code (8-10 digits, CH99 format, or wildcards)"
                              customHeader={
                                <HTSCodeToggleHeader
                                  codeType={codeType}
                                  onCodeTypeChange={setCodeType}
                                />
                              }
                            />
                          </Grid>
                          
                          {/* CH99 Code Change Modes */}
                          <Grid item xs={12}>
                            <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                              <Typography variant="body2" fontWeight="bold" gutterBottom>
                                Chapter 99 Code Handling (Optional)
                              </Typography>
                              
                              {/* Mode Selector */}
                              <FormControl component="fieldset" sx={{ mb: 2 }}>
                                <RadioGroup
                                  value={entry.ch99ChangeMode || 'none'}
                                  onChange={(e) => {
                                    const mode = e.target.value === 'none' ? undefined : e.target.value as CH99ChangeMode;
                                    updateDutyAdjustment(index, 'ch99ChangeMode', mode);
                                    if (mode === 'replace_all') {
                                      updateDutyAdjustment(index, 'newCH99CodeList', []);
                                      updateDutyAdjustment(index, 'ch99Transformations', []);
                                    } else if (mode === 'selective') {
                                      updateDutyAdjustment(index, 'newCH99CodeList', []);
                                      updateDutyAdjustment(index, 'ch99Transformations', []);
                                    }
                                  }}
                                  row
                                >
                                  <FormControlLabel value="none" control={<Radio size="small" />} label="No CH99 Changes" />
                                  <FormControlLabel value="replace_all" control={<Radio size="small" />} label="Replace All CH99 Codes" />
                                  <FormControlLabel value="selective" control={<Radio size="small" />} label="Selective Transformations" />
                                </RadioGroup>
                              </FormControl>

                              {/* Replace All Mode UI */}
                              {entry.ch99ChangeMode === 'replace_all' && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="caption" color="textSecondary" display="block" mb={1}>
                                    Specify the complete new list of CH99 codes. All existing CH99 codes will be replaced.
                                  </Typography>
                                  <MultiAutocomplete
                                    value={entry.newCH99CodeList || []}
                                    onChange={(codes) => updateCH99CodeList(index, codes)}
                                    label="New CH99 Code List"
                                    options={ch99Codes.map(opt => opt.value)}
                                    placeholder="Select CH99 codes to use..."
                                    helperText="Add as many CH99 codes as needed - rates will be summed"
                                  />
                                </Box>
                              )}

                              {/* Selective Mode UI */}
                              {entry.ch99ChangeMode === 'selective' && (
                                <Box sx={{ mt: 2 }}>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="caption" color="textSecondary">
                                      Transform specific CH99 codes. Other codes will be preserved.
                                    </Typography>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => addTransformation(index)}
                                      sx={{ fontSize: '0.7rem' }}
                                    >
                                      + Add Transformation
                                    </Button>
                                  </Stack>
                                  
                                  {entry.ch99Transformations && entry.ch99Transformations.map((transform, transformIndex) => (
                                    <Box key={transformIndex} sx={{ 
                                      mb: 1.5, 
                                      p: 1.5, 
                                      bgcolor: 'grey.50', 
                                      borderRadius: 1,
                                      border: '1px solid',
                                      borderColor: 'grey.300'
                                    }}>
                                      <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} sm={5}>
                                          <SingleAutocomplete
                                            label="From (Original CH99)"
                                            value={transform.from}
                                            options={ch99Codes.map(opt => opt.value)}
                                            onChange={(value) => updateTransformation(index, transformIndex, 'from', value)}
                                            placeholder="Select or enter CH99 code"
                                          />
                                        </Grid>
                                        <Grid item xs={12} sm={5}>
                                          <SingleAutocomplete
                                            label="To (New CH99)"
                                            value={transform.to}
                                            options={ch99Codes.map(opt => opt.value)}
                                            onChange={(value) => updateTransformation(index, transformIndex, 'to', value)}
                                            placeholder="Select or enter CH99 code"
                                          />
                                        </Grid>
                                        <Grid item xs={12} sm={2}>
                                          <IconButton
                                            onClick={() => removeTransformation(index, transformIndex)}
                                            size="small"
                                            color="error"
                                          >
                                            <CloseIcon fontSize="small" />
                                          </IconButton>
                                        </Grid>
                                      </Grid>
                                    </Box>
                                  ))}
                                  
                                  {(!entry.ch99Transformations || entry.ch99Transformations.length === 0) && (
                                    <Typography variant="caption" color="textSecondary" display="block" textAlign="center" py={1}>
                                      No transformations. Click "+ Add Transformation" to add one.
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </Box>
                          </Grid>
                        </>
                      )}

                      {/* Rate Adjustment Fields */}
                      {entry.operation === 'rate_adjustment' && (
                        <>
                          <Grid item xs={12} sm={4}>
                            <SingleAutocomplete
                              label="HTS Code"
                              value={entry.htsCode || ''}
                              options={htsOptions.map(opt => opt.value)}
                              onChange={(value) => updateDutyAdjustment(index, 'htsCode', value)}
                              placeholder={htsLoading ? "Loading codes..." : "Select from list or enter code"}
                              disabled={htsLoading}
                              helperText={htsValidationErrors[index] || "Code to modify (supports wildcards: 1234*)"}
                              error={!!htsValidationErrors[index]}
                              customHeader={
                                <HTSCodeToggleHeader
                                  codeType={codeType}
                                  onCodeTypeChange={setCodeType}
                                />
                              }
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Box>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <TextInput
                                  label="New Rate (0-10)"
                                  type="number"
                                  value={entry.newRate?.toString() || ''}
                                  handleChangeValue={(value) => {
                                    const rate = parseFloat(value) || 0;
                                    if (rate < 0 || rate > 10) {
                                      setFieldErrors(prev => ({
                                        ...prev,
                                        [`dutyRate_${index}`]: 'Rate must be between 0 and 10'
                                      }));
                                    } else {
                                      setFieldErrors(prev => {
                                        const { [`dutyRate_${index}`]: _, ...rest } = prev;
                                        return rest;
                                      });
                                    }
                                    updateDutyAdjustment(index, 'newRate', rate);
                                  }}
                                  placeholder="e.g., 0.05 for 5%"
                                  helperText={fieldErrors[`dutyRate_${index}`] || "Decimal format: 0.05 = 5%"}
                                  error={!!fieldErrors[`dutyRate_${index}`]}
                                />
                                <Tooltip title="Enter duty rate in decimal format. 0.05 = 5%, 0.10 = 10%">
                                  <InfoIcon color="action" fontSize="small" />
                                </Tooltip>
                              </Stack>
                            </Box>
                          </Grid>
                        </>
                      )}

                      {/* Remove Button */}
                      <Grid item xs={12} sm={1}>
                        <IconButton 
                          onClick={() => removeDutyAdjustment(index)}
                          size="small"
                          disabled={dutyAdjustments.length === 1}
                          color="error"
                        >
                          <CloseIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
                
                <Typography variant="caption" color="textSecondary">
                  <strong>Code Changes:</strong> Replace MFN HTS codes across matching records. Optionally modify CH99 codes:<br/>
                  • <em>Replace All:</em> Provide a complete new list of CH99 codes (unlimited)<br/>
                  • <em>Selective:</em> Transform specific CH99 codes while keeping others<br/>
                  <strong>Rate Adjustments:</strong> Modify duty rates for specific HTS codes.<br/>
                  Both support wildcards (e.g., 1234*) and include Chapter 99 codes.
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
      </Box>
    </ModalV2>
  );
};

export default ReconciliationConfigModal;